import { ChevronRight, Folder, Music, FileAudio, Download, Star, Clock, Search, GripVertical } from 'lucide-react';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ResizeHandle } from './ResizeHandle';
import { usePanelResize, PANEL_MIN_SIZES } from '../hooks/usePanelResize';
import { useWindowResize } from '../hooks/useWindowResize';

interface BrowserPanelProps {
  isMobile?: boolean;
  isTablet?: boolean;
}

export function BrowserPanel({ isMobile = false, isTablet = false }: BrowserPanelProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'files' | 'plugins' | 'favorites'>('files');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'Sample Packs': true,
    'Plugin Database': false,
    'Current Project': false,
  });
  
  // Panel resizing state (Requirements 8.1, 8.2, 8.3)
  const [panelWidth, setPanelWidth] = useState(isMobile ? 256 : isTablet ? 224 : 288);
  
  const { handleResize, handleResizeEnd } = usePanelResize({
    panelId: 'browser',
    minSize: PANEL_MIN_SIZES.browser,
    currentSize: panelWidth,
    onResize: setPanelWidth,
  });
  
  // Handle window resize for proportional scaling (Requirements 8.5)
  useWindowResize({
    panelId: 'browser',
    currentSize: panelWidth,
    onResize: setPanelWidth,
  });

  // Auto-collapse on mobile by default
  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [isMobile]);

  const toggleSection = (sectionName: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const fileItems = [
    { 
      name: 'Sample Packs', 
      icon: Folder, 
      children: [
        { name: 'Kick 01.wav', type: 'file', category: 'Drums' },
        { name: 'Snare 02.wav', type: 'file', category: 'Drums' },
        { name: 'Hi-Hat 03.wav', type: 'file', category: 'Drums' },
        { name: 'Bass 01.wav', type: 'file', category: 'Bass' },
        { name: 'Synth Lead 01.wav', type: 'file', category: 'Synths' },
        { name: 'Pad Warm.wav', type: 'file', category: 'Synths' },
        { name: 'Reverb Tail.wav', type: 'file', category: 'FX' },
        { name: 'Vocal Chop.wav', type: 'file', category: 'Vocals' },
      ] 
    },
    { 
      name: 'Presets', 
      icon: Music, 
      children: [
        { name: 'Bass Patch 01', type: 'file', category: 'Bass' },
        { name: 'Lead Synth', type: 'file', category: 'Lead' },
        { name: 'Pad Warm', type: 'file', category: 'Pad' },
        { name: 'Pluck Bright', type: 'file', category: 'Pluck' },
      ] 
    },
    { 
      name: 'FX', 
      icon: FileAudio, 
      children: [
        { name: 'Reverb Large', type: 'file', category: 'Reverb' },
        { name: 'Delay Ping Pong', type: 'file', category: 'Delay' },
        { name: 'EQ Bright', type: 'file', category: 'EQ' },
        { name: 'Compressor Punchy', type: 'file', category: 'Dynamics' },
      ] 
    },
  ];

  const recentFiles = [
    'kick_808.wav',
    'snare_tight.wav',
    'synth_bass_01.wav',
    'vocal_chop.wav',
  ];

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        className="w-8 bg-[#1f1f1f] border-r border-[#3a3a3a] hover:bg-[#252525] transition-colors flex items-center justify-center"
      >
        <ChevronRight size={16} />
      </button>
    );
  }

  return (
    <div 
      className="bg-[#1f1f1f] border-r border-[#3a3a3a] flex flex-row transition-all duration-300"
      style={{ width: `${panelWidth}px` }}
    >
      <div className="flex-1 flex flex-col overflow-hidden">
      <div className="h-10 bg-[#252525] flex items-center justify-between px-3 border-b border-[#3a3a3a]">
        <span className="text-xs">BROWSER</span>
        <button
          onClick={() => setCollapsed(true)}
          className="hover:bg-[#3a3a3a] p-1 rounded transition-colors"
        >
          <ChevronRight size={14} />
        </button>
      </div>

      {/* Search bar */}
      <div className="p-2 border-b border-[#3a3a3a]">
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 opacity-50" />
          <input
            type="text"
            placeholder="Search samples..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-[#2a2a2a] border border-[#3a3a3a] rounded px-7 py-1.5 text-xs placeholder:opacity-50 focus:outline-none focus:border-[#ff6b00]"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#3a3a3a] bg-[#252525]">
        <button
          onClick={() => setActiveTab('files')}
          className={`flex-1 px-3 py-2 text-[10px] ${
            activeTab === 'files' ? 'bg-[#1f1f1f] border-b-2 border-[#ff6b00]' : 'opacity-50'
          }`}
        >
          FILES
        </button>
        <button
          onClick={() => setActiveTab('plugins')}
          className={`flex-1 px-3 py-2 text-[10px] ${
            activeTab === 'plugins' ? 'bg-[#1f1f1f] border-b-2 border-[#ff6b00]' : 'opacity-50'
          }`}
        >
          PLUGINS
        </button>
        <button
          onClick={() => setActiveTab('favorites')}
          className={`flex-1 px-3 py-2 text-[10px] ${
            activeTab === 'favorites' ? 'bg-[#1f1f1f] border-b-2 border-[#ff6b00]' : 'opacity-50'
          }`}
        >
          FAVORITES
        </button>
      </div>
      
      <div className="flex-1 overflow-y-auto p-2 text-sm">
        {activeTab === 'files' && (
          <>
            {fileItems.map((item) => (
              <div key={item.name} className="mb-2">
                {/* Section Header with Collapse/Expand */}
                <button
                  onClick={() => toggleSection(item.name)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-[#2a2a2a] rounded cursor-pointer group transition-colors"
                >
                  <motion.div
                    animate={{ rotate: expandedSections[item.name] ? 90 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronRight size={12} className="opacity-50" />
                  </motion.div>
                  <item.icon size={14} className="text-[#ff6b00]" />
                  <span className="flex-1 text-left">{item.name}</span>
                  <span className="text-[10px] opacity-50">{item.children.length}</span>
                </button>
                
                {/* Collapsible Content with Animation */}
                <AnimatePresence>
                  {expandedSections[item.name] && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="ml-6 mt-1 space-y-0.5">
                        {item.children.map((child) => (
                          <div
                            key={child.name}
                            className="px-2 py-1 hover:bg-[#2a2a2a] rounded cursor-pointer text-xs opacity-70 hover:opacity-100 flex items-center gap-2 group transition-colors"
                          >
                            {/* Drag Indicator (visual only) */}
                            <GripVertical size={10} className="opacity-0 group-hover:opacity-30 transition-opacity cursor-grab" />
                            <FileAudio size={10} className="opacity-50" />
                            <span className="flex-1">{child.name}</span>
                            <span className="text-[10px] opacity-0 group-hover:opacity-50 transition-opacity">
                              {child.category}
                            </span>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </>
        )}

        {activeTab === 'plugins' && (
          <div className="space-y-1">
            {['Triple Oscillator', 'FM Synth Pro', 'Sampler', 'Wavetable Synth', 'Drum Synthesizer'].map((plugin) => (
              <div
                key={plugin}
                className="px-2 py-2 hover:bg-[#2a2a2a] rounded cursor-pointer flex items-center gap-2 group transition-colors"
              >
                <GripVertical size={10} className="opacity-0 group-hover:opacity-30 transition-opacity cursor-grab" />
                <Music size={14} className="text-[#ff6b00]" />
                <span className="text-xs flex-1">{plugin}</span>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'favorites' && (
          <div className="space-y-1">
            {['Bass Patch 01', 'Lead Synth', 'Pad Warm', 'Pluck Bright'].map((fav) => (
              <div
                key={fav}
                className="px-2 py-2 hover:bg-[#2a2a2a] rounded cursor-pointer flex items-center gap-2 group transition-colors"
              >
                <GripVertical size={10} className="opacity-0 group-hover:opacity-30 transition-opacity cursor-grab" />
                <Star size={14} className="text-yellow-500 fill-yellow-500" />
                <span className="text-xs flex-1">{fav}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent files section */}
      <div className="border-t border-[#3a3a3a] bg-[#252525] p-2">
        <div className="flex items-center gap-2 mb-2">
          <Clock size={12} className="opacity-50" />
          <span className="text-[10px] opacity-70">RECENT</span>
        </div>
        <div className="space-y-0.5">
          {recentFiles.map((file) => (
            <div
              key={file}
              className="px-2 py-1 hover:bg-[#2a2a2a] rounded cursor-pointer text-[10px] opacity-70 hover:opacity-100 truncate"
            >
              {file}
            </div>
          ))}
        </div>
      </div>

      {/* Download section */}
      <div className="border-t border-[#3a3a3a] bg-[#1a1a1a] p-3">
        <button className="w-full bg-[#ff6b00] hover:bg-[#ff8b30] py-2 rounded flex items-center justify-center gap-2 text-xs transition-colors">
          <Download size={14} />
          Get More Sounds
        </button>
      </div>
      </div>
      
      {/* Resize Handle (Requirements 8.1) */}
      {!isMobile && (
        <ResizeHandle
          direction="horizontal"
          onResize={handleResize}
          onResizeEnd={handleResizeEnd}
        />
      )}
    </div>
  );
}
